import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import Yup from 'yup';
import Steps, { Step } from 'base/Steps';
import { closeModal } from 'modal/store/actions';
import theme, { textBold } from 'theme/theme';
import MainTitle from 'base/components/MainTitle';
import SubmitButton from 'base/components/forms/SubmitButton';
import FormikInputField from 'base/components/forms/formik/FormikInputField';
import Fieldset from 'base/components/forms/Fieldset';
import Legend from 'base/components/forms/Legend';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import Button from 'base/components/Button';
import { AlertCircleIcon, CheckCircleIcon } from 'base/components/Icon';
import api from '../../../api';
import {
  init2Fa,
  enable2Fa,
} from '../../store/auth/actions';
import {
  get2FaQrCodeUrl,
  getEnable2FaHasError,
  get2FaSecret,
  getAuthUser,
} from '../../store/auth/selectors';

const ErrorMessage = styled.div`
  margin: 0 0 2rem 0;
  ${textBold};
  color: ${theme.colors.primary};
`;

const androidUrl = 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2';
const iOsUrl = 'http://appstore.com/googleauthenticator';

class MyUserSecuritySettingsModal extends React.Component {
  state = {
    currentStep: 0,
    passwordIsConfirmed: false,
    passwordIsIncorrect: false,
    password: '',
  };

  componentDidMount() {
  }

  nextStep = () => this.setState((prevState) => ({ currentStep: prevState.currentStep + 1 }));

  handleStep0 = () => {
    this.props.init2Fa(this.state.password);
    this.nextStep();
  };

  verifyPassword = (password) => {
    api.auth.verifyPassword(password).toPromise()
      .then(() => {
        this.setState({ passwordIsConfirmed: true, password });
      })
      .catch(() => {
        this.setState({ passwordIsConfirmed: false, passwordIsIncorrect: true });
      });
  };

  render() {
    const {
      enable2FaHasError, user, qrCodeUrl, secret, enable2Fa, // eslint-disable-line
    } = this.props;
    const {
      currentStep, passwordIsConfirmed, password, passwordIsIncorrect,
    } = this.state;

    const step0 = (
      <>
        <FieldWrapper>
            Download Authenticator for
          {' '}
          <a href={iOsUrl} target="_blank" rel="noopener noreferrer">iOS</a>
            &nbsp;or
          {' '}
          <a href={androidUrl} target="_blank" rel="noopener noreferrer">Android</a>
.
        </FieldWrapper>
        <Button variant="secondary" onClick={this.handleStep0}>
            Next
        </Button>
      </>
    );

    const step1 = (
      <>
        <FieldWrapper>
            Use the Authenticator app to scan this QR code:
        </FieldWrapper>
        {qrCodeUrl && (
          <FieldWrapper>
            <img src={qrCodeUrl} alt="Authenticator QR code" />
          </FieldWrapper>
        )}
        <Button variant="secondary" onClick={this.nextStep}>
            Next
        </Button>
      </>
    );

    const description2FaIsActive = (
      <>
        <FieldWrapper>
          <CheckCircleIcon />
          <strong> You are already using 2-factor authentication!</strong>
        </FieldWrapper>
      </>
    );
    const description2FaIsDisabled = (
      <>
        <FieldWrapper>
          <AlertCircleIcon />
          <strong> You are not using 2-factor authentication!</strong>
        </FieldWrapper>

        <FieldWrapper>
            Increase the security of your account by using 2-factor
            authentication with the Authenticator app. Each time you sign
            into your account, you&apos;ll need to provide your
            password and a verification code.
        </FieldWrapper>
      </>
    );

    const form = ({ errors, isSubmitting, values }) => ( // eslint-disable-line
      <Form>
        <Fieldset>
          <Legend>2-factor authentication</Legend>

          {user.isUsing2FA
            ? description2FaIsActive
            : (
              <>
                {description2FaIsDisabled}
                {passwordIsConfirmed
                  ? (
                    <>
                      <FieldWrapper>
                        <strong>
                      Set up 2-factor authentication in 3 simple steps:
                        </strong>
                      </FieldWrapper>


                      <Steps current={currentStep} style={{ marginBottom: '1rem' }}>
                        <Step title="Download App" />
                        <Step title="Scan QR code" />
                        <Step title="Activate" />
                      </Steps>

                      {currentStep === 0 && step0}
                      {currentStep === 1 && step1}
                      {currentStep === 2 && (
                      <>

                        {enable2FaHasError && (
                        <ErrorMessage style={{
                          color: '#fff',
                          background: theme.colors.primary,
                          padding: '10px',
                          margin: '10px 0',
                        }}
                        >
                      Invalid verification code. Please try again.
                        </ErrorMessage>
                        )}

                        <Field
                          type="text"
                          name="otp"
                          label="Enter the verification code generated by
                                    your app to finish the setup."
                          placeholder="Verification code"
                          component={FormikInputField}
                        />

                        <FieldWrapper>
                      Note: for security reasons, you will be logged out
                      after enabling 2-factor authentication
                        </FieldWrapper>

                        <SubmitButton type="submit" disabled={false}>
                                        Activate 2-factor authentication
                        </SubmitButton>
                      </>
                      )}
                    </>
                  )
                  : (
                    <>
                      <strong>Confirm identity</strong>
                      {passwordIsIncorrect && (
                      <ErrorMessage style={{
                        color: '#fff',
                        background: theme.colors.primary,
                        padding: '10px',
                        margin: '10px 0',
                      }}
                      >
                          Invalid password. Please try again.
                      </ErrorMessage>
                      )}
                      <Field
                        type="password"
                        name="password"
                        label="Enter your password to confirm your identity
                      before making changes to your account"
                        placeholder="Password"
                        component={FormikInputField}
                      />
                      <Button
                        variant="secondary"
                        onClick={() => this.verifyPassword(values.password)}
                      >
                      Verify password
                      </Button>
                    </>
                  )}

              </>
            )}


        </Fieldset>


      </Form>
    );

    return (
      <>
        <MainTitle withSpaceBelow>Security settings</MainTitle>

        {user && (
        <Formik
          initialValues={{
            otp: '',
            password: password || '',
          }}
          validationSchema={
                  Yup.object().shape({
                    otp: Yup.string()
                      .required('Verification code is required.'),
                    password: Yup.string()
                      .required('Password is required.'),
                  })
                }
          render={form}
          onSubmit={(values) => {
            enable2Fa(values.otp, secret);
          }}
        />
        )}

      </>
    );
  }
}
MyUserSecuritySettingsModal.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  init2Fa: PropTypes.func.isRequired,
  enable2Fa: PropTypes.func.isRequired,
  enable2FaHasError: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  user: getAuthUser(state),
  qrCodeUrl: get2FaQrCodeUrl(state),
  secret: get2FaSecret(state),
  enable2FaHasError: getEnable2FaHasError(state),
});

const mapDispatchToProps = {
  closeModal,
  init2Fa,
  enable2Fa,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MyUserSecuritySettingsModal);
